import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";

import PageHeader from "components/PageHeader";
import { useAuth } from "contexts/AuthContext";
import { getStorageUrl } from "config/api";

const CURRENCIES = ["GHS", "USD", "NGN", "GBP", "EUR", "ZAR", "KES"];

function SectionCard({ title, subtitle, children }) {
  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />
      {children}
    </Card>
  );
}

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node,
};

function Settings() {
  const { user, church, updateProfile, uploadChurchLogo, changePassword } = useAuth();
  const navigate = useNavigate();

  const [churchForm, setChurchForm] = useState({
    name: "",
    sms_sender_id: "",
    sms_api_token: "",
    currency: "GHS",
    welfare_amount: "",
    biometric_device_ip: "",
    biometric_device_port: "",
    biometric_middleware_url: "",
  });
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [accountForm, setAccountForm] = useState({
    name: "",
    email: "",
    email_notifications: true,
    sms_notifications: true,
  });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  const [isSavingChurch, setIsSavingChurch] = useState(false);
  const [isSavingAccount, setIsSavingAccount] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  useEffect(() => {
    if (church) {
      setChurchForm({
        name: church.name || "",
        sms_sender_id: church.sms_sender_id || "",
        // sms_api_token is never returned by the API (hidden on the Church
        // model) — always starts blank; saving only sends it if re-typed.
        sms_api_token: "",
        currency: church.currency || "GHS",
        welfare_amount: church.welfare_amount ?? "",
        biometric_device_ip: church.biometric_device_ip || "",
        biometric_device_port: church.biometric_device_port ?? "",
        biometric_middleware_url: church.biometric_middleware_url || "",
      });
    }
  }, [church]);

  useEffect(() => {
    if (user) {
      setAccountForm({
        name: user.name || "",
        email: user.email || "",
        email_notifications: user.email_notifications ?? true,
        sms_notifications: user.sms_notifications ?? true,
      });
    }
  }, [user]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const handleSaveChurch = async () => {
    setIsSavingChurch(true);
    try {
      // sms_api_token can never be read back (hidden on the Church model),
      // so only send it when the admin actually typed a new value — an
      // empty string here would otherwise silently clear it on every save.
      const { sms_api_token, ...rest } = churchForm;
      const payload = sms_api_token ? { ...rest, sms_api_token } : rest;
      await updateProfile({}, payload);
      showToast("success", "Church settings saved.");
    } catch (err) {
      showToast("error", err.message || "Failed to save church settings.");
    } finally {
      setIsSavingChurch(false);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      await uploadChurchLogo(file);
      showToast("success", "Church logo updated.");
    } catch (err) {
      showToast("error", err.message || "Failed to upload church logo.");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveAccount = async () => {
    setIsSavingAccount(true);
    try {
      await updateProfile(accountForm, {});
      showToast("success", "Account settings saved.");
    } catch (err) {
      showToast("error", err.message || "Failed to save account settings.");
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.password !== passwordForm.password_confirmation) {
      showToast("error", "New password and confirmation do not match.");
      return;
    }
    setIsSavingPassword(true);
    try {
      await changePassword(
        passwordForm.current_password,
        passwordForm.password,
        passwordForm.password_confirmation
      );
      setPasswordForm({ current_password: "", password: "", password_confirmation: "" });
      showToast("success", "Password changed successfully.");
    } catch (err) {
      showToast("error", err.message || "Failed to change password.");
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <Box>
      <PageHeader title="Settings" subtitle="Manage your church, account, and branches." />

      <Grid container justifyContent="center">
        <Grid item xs={12} md={8}>
          <SectionCard
            title="Church Settings"
            subtitle="Branding and identity shown across SMS and reports."
          >
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Avatar
                src={getStorageUrl(church?.logo)}
                variant="rounded"
                sx={{ width: 64, height: 64, bgcolor: "grey.200" }}
              >
                {(church?.abbreviation || church?.name || "?").slice(0, 2).toUpperCase()}
              </Avatar>
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  startIcon={isUploadingLogo ? <CircularProgress size={16} /> : <Icon>upload</Icon>}
                  disabled={isUploadingLogo}
                >
                  {isUploadingLogo ? "Uploading..." : "Change Logo"}
                  <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                  PNG or JPG, up to 2MB. Shown in the sidebar and on printed reports.
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Church Name"
                  value={churchForm.name}
                  onChange={(e) => setChurchForm({ ...churchForm, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Currency"
                  value={churchForm.currency}
                  onChange={(e) => setChurchForm({ ...churchForm, currency: e.target.value })}
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMS Sender ID"
                  value={churchForm.sms_sender_id}
                  onChange={(e) => setChurchForm({ ...churchForm, sms_sender_id: e.target.value })}
                  helperText="Up to 11 letters/numbers, shown as the 'from' name on SMS sent to members."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="SMS API Token"
                  value={churchForm.sms_api_token}
                  onChange={(e) => setChurchForm({ ...churchForm, sms_api_token: e.target.value })}
                  placeholder={
                    church?.has_sms_token ? "•••••••• (set — leave blank to keep)" : "Not set"
                  }
                  helperText="Your mycsms.com gateway token. Leave blank to keep the current one."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Monthly Welfare Amount"
                  value={churchForm.welfare_amount}
                  onChange={(e) => setChurchForm({ ...churchForm, welfare_amount: e.target.value })}
                  helperText="Expected welfare contribution per member, per month. Used to flag partial/owed welfare in the Welfare report."
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleSaveChurch} disabled={isSavingChurch}>
                {isSavingChurch ? "Saving..." : "Save Church Settings"}
              </Button>
            </Box>
          </SectionCard>

          <SectionCard
            title="Biometric Device"
            subtitle="Each church can point to its own fingerprint scanner for attendance check-in."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Device IP Address"
                  value={churchForm.biometric_device_ip}
                  onChange={(e) =>
                    setChurchForm({ ...churchForm, biometric_device_ip: e.target.value })
                  }
                  helperText="e.g. 192.168.1.50 — the ZKTeco scanner's IP on the local network."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Device Port"
                  value={churchForm.biometric_device_port}
                  onChange={(e) =>
                    setChurchForm({ ...churchForm, biometric_device_port: e.target.value })
                  }
                  helperText="Default 4370"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Biometric Middleware URL"
                  value={churchForm.biometric_middleware_url}
                  onChange={(e) =>
                    setChurchForm({ ...churchForm, biometric_middleware_url: e.target.value })
                  }
                  helperText="URL of the Python ZKTeco bridge running on your local network — e.g. http://192.168.90.100:5000. This must be reachable from the browser doing enrollment."
                  placeholder="http://192.168.90.100:5000"
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleSaveChurch} disabled={isSavingChurch}>
                {isSavingChurch ? "Saving..." : "Save Church Settings"}
              </Button>
            </Box>
          </SectionCard>

          <SectionCard
            title="Account Settings"
            subtitle="Your profile and notification preferences."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={accountForm.name}
                  onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={accountForm.email}
                  onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={accountForm.email_notifications}
                      onChange={(e) =>
                        setAccountForm({ ...accountForm, email_notifications: e.target.checked })
                      }
                    />
                  }
                  label="Email notifications"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={accountForm.sms_notifications}
                      onChange={(e) =>
                        setAccountForm({ ...accountForm, sms_notifications: e.target.checked })
                      }
                    />
                  }
                  label="SMS notifications"
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleSaveAccount} disabled={isSavingAccount}>
                {isSavingAccount ? "Saving..." : "Save Account Settings"}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Change Password
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, current_password: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  value={passwordForm.password_confirmation}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, password_confirmation: e.target.value })
                  }
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleChangePassword}
                disabled={
                  isSavingPassword || !passwordForm.current_password || !passwordForm.password
                }
              >
                {isSavingPassword ? "Updating..." : "Change Password"}
              </Button>
            </Box>
          </SectionCard>

          <SectionCard title="Branch Management" subtitle="View and manage your church's branches.">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                View branch performance, members, and income from one place.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon>account_tree</Icon>}
                onClick={() => navigate("/branches")}
              >
                Go to Branches
              </Button>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;
